
import { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { databases } from '@/lib/appwrite';
import { Query } from 'appwrite';
import { cn } from '@/lib/utils';

const DB_ID = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID;
const COLLECTION_ID = import.meta.env.VITE_BLOCKCHAIN_BRANDS;
// Normalizar texto (quitar tildes)
const normalize = (text: string): string => {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};


// Función de búsqueda inteligente
const smartSearch = (orgName: string, searchTerm: string): number => {
    const name = normalize(orgName.toLowerCase());
    const term = normalize(searchTerm.toLowerCase().trim());


    if (!term) return 0;

    // Coincidencia exacta completa = máxima prioridad
    if (name === term) return 100;

    // Empieza con el término = alta prioridad
    if (name.startsWith(term)) return 90;

    // Búsqueda por iniciales (ej: "lab eco" → "Laboratorio Ecuador")
    const words = name.split(/\s+/);
    const initials = words.map(w => w[0]).join('');
    if (initials.includes(term.replace(/\s+/g, ''))) return 70;


    // Contiene todas las palabras del término
    const searchWords = term.split(/\s+/);
    const allWordsMatch = searchWords.every(word => name.includes(word));
    if (allWordsMatch) return 60;



    // Contiene el término completo
    if (name.includes(term)) return 50;

    // Contiene alguna palabra
    const someWordMatch = searchWords.some(word => name.includes(word));
    if (someWordMatch) return 30;

    return 0; // No coincide
};


interface Organization {
    id: string;
    name: string;
    //   address?: string;
    //   city?: string;
}

interface OrganizationComboboxProps {
    value?: string; // Solo el ID
    onChange?: (id: string, name: string) => void;
    placeholder?: string;
    disabled?: boolean;
    hasError?: boolean
}

export const BrandCombobox = ({
    value = '',
    onChange,
    placeholder = 'Seleccionar marca...',
    disabled = false,
    hasError = false
}: OrganizationComboboxProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Cargar organizaciones al montar
    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                setIsLoading(true);
                setFetchError(false);

                const response = await databases.listDocuments(
                    DB_ID,
                    COLLECTION_ID,
                    [
                        Query.select(['$id', 'name']),
                        Query.limit(5000),
                        Query.orderAsc('name')
                    ]
                );

                const orgs = response.documents.map(doc => ({
                    id: doc.$id,
                    name: doc.name,

                }));

                setOrganizations(orgs);
                setFilteredOrgs(orgs);
            } catch (error) {
                console.error('Error loading organizations:', error);
                setFetchError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrganizations();
    }, []);

    // Sincronizar con value externo
    useEffect(() => {
        if (value && organizations.length > 0) {
            const org = organizations.find(o => o.id === value);
            if (org) setSelectedOrg(org);
        } else {
            setSelectedOrg(null);
        }
    }, [value, organizations]);

    // Filtrar organizaciones con búsqueda inteligente
    useEffect(() => {
        if (!search.trim()) {
            setFilteredOrgs(organizations);
            return;
        }

        // Filtrar y ordenar por relevancia
        const scored = organizations
            .map(org => ({
                ...org,
                score: smartSearch(org.name, search)
            }))
            .filter(org => org.score > 0)
            .sort((a, b) => b.score - a.score);

        setFilteredOrgs(scored);
    }, [search, organizations]);

    // Cerrar al hacer click afuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleSelect = (org: Organization) => {
        setSelectedOrg(org);
        onChange?.(org.id, org.name);
        setOpen(false);
        setSearch('');
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedOrg(null);
        onChange?.('', '');
        setSearch('');
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    if (isLoading) {
        return (
            <div className="w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground">
                Cargando marcas...
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="flex items-center gap-2 w-full px-3 py-2 text-sm border rounded-md bg-background">
                <span className="text-red-500 flex-1">Error al cargar</span>
                <button
                    onClick={handleRefresh}
                    className="px-2 py-1 text-xs bg-secondary hover:bg-secondary/80 rounded transition-colors"
                >
                    🔄 Refrescar
                </button>
            </div>
        );
    }

    return (
        <div ref={dropdownRef} className="relative w-full">
            <button
                type="button"
                onClick={() => !disabled && setOpen(!open)}
                disabled={disabled}
                className={cn(
                    "flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md",
                    "bg-background text-foreground",
                    "hover:bg-accent hover:text-accent-foreground transition-colors",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    open && "ring-2 ring-ring ring-offset-2",
                    hasError && "border-red-400"  
                )}
            >
                <span className={cn(
                    "truncate",
                    !selectedOrg && "text-muted-foreground"
                )}>
                    {selectedOrg ? selectedOrg.name : placeholder}
                </span>

                <div className="flex items-center gap-1 ml-2 shrink-0">
                    {selectedOrg && !disabled && (
                        <X
                            className="h-4 w-4 opacity-50 hover:opacity-100"
                            onClick={handleClear}
                        />
                    )}
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                </div>
            </button>

            {open && (
                <div className="absolute z-50 w-full mt-1 border rounded-md shadow-lg bg-popover text-popover-foreground">
                    {/* Input de búsqueda */}
                    <div className="p-2 border-b">
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar..."
                            className="w-full px-3 py-1.5 text-sm bg-background text-foreground border rounded-md outline-none focus:ring-2 focus:ring-ring"
                            autoFocus
                        />
                    </div>

                    {/* Lista de opciones */}
                    <div className="max-h-60 overflow-auto p-1">
                        {filteredOrgs.length === 0 ? (
                            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                No se encontraron resultados
                            </div>
                        ) : (
                            filteredOrgs.map((org) => (
                                <button
                                    key={org.id}
                                    type="button"
                                    onClick={() => handleSelect(org)}
                                    className={cn(
                                        "flex items-center justify-between w-full px-3 py-2 text-sm rounded-sm",
                                        "hover:bg-accent hover:text-accent-foreground transition-colors",
                                        "text-left cursor-pointer",
                                        selectedOrg?.id === org.id && "bg-accent"
                                    )}
                                >
                                    <span className="truncate">{org.name}</span>
                                    {selectedOrg?.id === org.id && (
                                        <Check className="h-4 w-4 shrink-0 ml-2" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

